/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { TFunction } from 'i18next';
import { RootState } from 'app/AppStore';
import { ErrorWithLabel } from 'components/DataContainer';
import {
  fetchPromotionBannersRequestSaga,
  fetchSavePromotionRequestSaga,
  getCreatePromotion,
  getPublishedStatus,
  setCreatePromotion,
  setInitialCreatePromotion,
} from 'domains/promotion/Promotion.store';
import { CreatePromotionModel } from 'domains/promotion/Promotion.types';
import { createPromotionPadding } from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/CreatePromotion.styled';
import CreatePromotionHeader from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/CreatePromotionHeader';
import DiscountTypeStep from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/DiscountTypeStep/DiscountTypeStep';
import { ProductStep } from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/PriceOptionStep/ProductStep';
import PromotionDetailsStep from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/PromotionDetailsStep/PromotionDetailsStep';
import { PublishStep } from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/PublishStep/PublishStep';
import {
  validateStepProduct,
  validateStepPromotionDetails,
  validateStepPromotionType,
} from 'pages/BackOfficePage/BackOfficeCategories/Promotion/CreatePromotion/StepsValidationUtils/StepsValidationUtils';
import { CREATE_NEW } from 'pages/BackOfficePage/BackOfficePage.type';
import {
  BlackButton,
  CenteredSpin,
  CenterFlex,
  Flex,
  MarginBox,
  notifyTop,
  StepData,
  Steps,
  Text,
  YellowButton,
} from 'UI';
import { ERROR, hasData, LOADING, NOT_FOUND } from 'utils';

const getStepsData = (t: TFunction): StepData[] => {
  return [
    { title: t('promotion.create_new.title.promotion_details', 'Promotion details') },
    { title: t('promotion.create_new.title.product', 'Product') },
    {
      title: t('promotion.create_new.title.publish', 'Publish'),
    },
  ];
};

const CreatePromotion = ({ handleCancel, startStep }: { handleCancel: () => void; startStep?: number }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(startStep ?? -1);
  const dispatch = useDispatch();
  const history = useHistory();
  const promotion = useSelector(getCreatePromotion);
  const publishedStatus = useSelector((state: RootState) => getPublishedStatus(state, promotion.promotionId));

  const nextStep = () => {
    setCurrentStep((prevState) => prevState + 1);
  };

  const prevStep = () => {
    setCurrentStep((prevState) => prevState - 1);
  };

  const setPromotion = (promotionToCreate: CreatePromotionModel) => {
    if (promotion.isUpdated === false) {
      dispatch(setCreatePromotion({ ...promotionToCreate, isUpdated: true }));
    } else {
      dispatch(setCreatePromotion(promotionToCreate));
    }
  };

  const handlePublish = () => {
    dispatch(fetchSavePromotionRequestSaga({ shouldPublish: true }));
  };

  const createNext = () => {
    dispatch(setInitialCreatePromotion());
    setCurrentStep(-1);
  };

  const validateStep = () => {
    const {
      title,
      catchLine,
      promotionType,
      publicationStart,
      publicationEnd,
      targetAudience,
      discountType = '',
      commercialFamilyCodes = [],
      uploadedReferencesFromFiles = [],
      references = [],
      targetAudienceType,
      additionalInformation,
      duplicatePromotionIds,
    } = promotion;

    switch (currentStep) {
      case -1: {
        return validateStepPromotionType(promotionType);
      }
      case 0: {
        return validateStepPromotionDetails(
          publicationStart,
          publicationEnd,
          targetAudience,
          targetAudienceType,
          promotionType,
          title,
          catchLine,
        );
      }
      case 1:
        return validateStepProduct(
          references,
          commercialFamilyCodes,
          uploadedReferencesFromFiles,
          duplicatePromotionIds,
          additionalInformation,
          promotionType,
          discountType,
        );
      default: {
        return false;
      }
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case -1:
        return <DiscountTypeStep promotion={promotion} setPromotion={setPromotion} />;
      case 0:
        return <PromotionDetailsStep promotion={promotion} setPromotion={setPromotion} />;
      case 1:
        return <ProductStep promotion={promotion} setPromotion={setPromotion} />;
      case 2:
        return <PublishStep promotion={promotion} setPromotion={setPromotion} />;
      default:
        return <></>;
    }
  };

  const getIsPublishDisabled = () => {
    switch (promotion.promotionType) {
      case 'DISCOUNT':
      case 'BANNER':
        return hasData(publishedStatus) || !promotion.image;
      default:
        return hasData(publishedStatus) && publishedStatus;
    }
  };

  const renderPublishButtons = () => {
    switch (publishedStatus) {
      case LOADING:
        return <CenteredSpin size={'small'} />;
      case ERROR:
        return (
          <MarginBox my={5}>
            <ErrorWithLabel
              label={t(
                'common.publish.backend_error',
                'Error occurred during promotion creation, please try again later.',
              )}
              narrow
            />
          </MarginBox>
        );
      case NOT_FOUND:
      case undefined:
      case false:
        return (
          <BlackButton stretch onClick={handlePublish} disabled={getIsPublishDisabled()} data-cy={'publish-button'}>
            <CenterFlex>{t('common.publish', 'Publish')}</CenterFlex>
          </BlackButton>
        );
      default:
        return (
          <YellowButton stretch onClick={createNext} data-cy={'create-another-button'}>
            <CenterFlex>{t('common.create_another', 'Create another')}</CenterFlex>
          </YellowButton>
        );
    }
  };

  const getButton = () => {
    switch (currentStep) {
      case 0:
      case 1:
        return (
          <BlackButton onClick={nextStep} disabled={!validateStep()} data-cy={'next-button'}>
            <CenterFlex>{t('common.next', 'Next')}</CenterFlex>
          </BlackButton>
        );
      case 2:
        return renderPublishButtons();
      default:
        return (
          <BlackButton onClick={nextStep} disabled={!validateStep()} data-cy={'start-button'}>
            <CenterFlex>{t('common.start', 'Start')}</CenterFlex>
          </BlackButton>
        );
    }
  };

  const renderSaveInfo = () => {
    if (promotion.status === 'PUBLISHED' || promotion.status === 'PLANNED') {
      return null;
    }
    return (
      <Flex>
        <Text type="lg" disableGutter data-cy={'all-changes-save-text'}>
          {t('promotion.create_new.save_note', 'Note that all changes will automatically be saved')}
        </Text>
      </Flex>
    );
  };

  useEffect(() => {
    document.getElementById('create-promotion-step-container')?.scrollTo({ top: 0 });
  }, [currentStep]);

  useEffect(() => {
    if (hasData(publishedStatus)) {
      const isActive = (link: string) => history.location.pathname.includes(link);
      if (publishedStatus === true) {
        dispatch(fetchPromotionBannersRequestSaga());
        const message = isActive(CREATE_NEW)
          ? t('backoffice.promotion.creation_result.success', 'Promotion created')
          : t('backoffice.promotion.edit_result.success', 'Promotion edited');
        const description = isActive(CREATE_NEW) ? (
          <span data-cy={'promotion-success-created'}>
            {t(
              'backoffice.promotion.creation_result.success.description',
              'The publication of your promotion has been taken into account and will soon be displayed online on Rpartstore',
            )}
          </span>
        ) : (
          <span data-cy={'promotion-success-edited'}>
            {t(
              'backoffice.promotion.edit_result.success.description',
              'The edit of your promotion has been taken into account and will be displayed online on Rpartstore',
            )}
          </span>
        );
        notifyTop('success', message, description);
      } else {
        const message = isActive(CREATE_NEW)
          ? t('backoffice.promotion.creation_result.failed', 'Promotion not created')
          : t('backoffice.promotion.edit_result.failed', 'Promotion not edited');
        notifyTop(
          'warning',
          message,
          <span data-cy={'promotion-failed-to-create'}>
            {t('backoffice.promotion.creation_result.failed.description', 'Promotion was not published')}
          </span>,
        );
      }
    }
  }, [publishedStatus, dispatch, history, t]);

  return (
    <Flex direction={'column'} minHeight={650} maxHeight={650}>
      <CreatePromotionHeader currentStep={currentStep} prevStep={prevStep} handleCancel={handleCancel} />
      <Flex overflowY={'scroll'}>
        <Flex
          size={3}
          direction={'column'}
          padding={createPromotionPadding}
          overflowY={'scroll'}
          id={'create-promotion-step-container'}
        >
          {renderStep()}
        </Flex>
        <Flex size={1} direction={'column'} justify={'space-between'} padding={createPromotionPadding} maxHeight={590}>
          {currentStep >= 0 ? (
            <>
              <Steps
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                data={getStepsData(t)}
                direction={'vertical'}
                gap={90}
                clickable
                data-cy={'steps-visual'}
              />
              {renderSaveInfo()}
            </>
          ) : (
            <Flex />
          )}
          {getButton()}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default CreatePromotion;
